/**
 * Hook para leitura das configurações globais da plataforma
 * (migration 0046: `public.platform_settings`).
 *
 * Usado na renderização de propostas (cabeçalho "KM Serviços
 * Administrativos LTDA", CNPJ, responsável, etc.) e em contratos
 * futuros. Key-value JSON — cada chave tem seu próprio valor parseado.
 *
 * RLS: `platform_settings_all_superadmin` — só Superadmin lê/escreve.
 * Em consumers públicos (rota `/proposta/:token`), a edge function
 * já injeta os dados no response (não precisa chamar este hook).
 */
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { supabase } from '../../lib/supabase.js';

export interface ContratadaSettings {
  razao_social: string;
  cnpj: string;
  nome_fantasia: string;
  cidade_uf: string;
  email_contato: string;
  responsavel_nome: string;
  responsavel_cargo: string;
}

export interface ProposalFooterSettings {
  institutional_text: string;
}

/** Textos do corpo da proposta que Karla edita via Superadmin
 * (PR #150 + migration 0053). Todos strings simples — templates
 * com placeholders são substituídos no render via `replaceTokens`.
 *
 * `about_us_paragraphs` é string único com `\n\n` separando parágrafos
 * (consumer faz `.split('\n\n')`).
 * `considerations_bullets` é string único com `\n` separando bullets
 * (consumer faz `.split('\n')`). */
export interface ProposalCopySettings {
  hero_title: string;
  hero_intro_template: string;
  about_us_title: string;
  about_us_paragraphs: string;
  considerations_bullets: string;
  /** Bullet de Considerações que aparece SOMENTE em propostas com
   * consultoria (proposal_type=consulting ou saas_consulting). Tokens:
   * {consulting_duration_months}, {saas_cobranca_note}. Migration 0054. */
  post_consulting_terms_template: string;
}

export interface PlatformSettings {
  contratada: ContratadaSettings;
  proposal_footer: ProposalFooterSettings;
  proposal_copy: ProposalCopySettings;
}

const settingRowSchema = z.object({
  key: z.string(),
  value: z.unknown(),
});

/** Extrai um string JSON da row ou retorna default. */
function extractString(map: Map<string, unknown>, key: string, fallback = ''): string {
  const v = map.get(key);
  return typeof v === 'string' ? v : fallback;
}

/** Compõe o shape tipado a partir das rows crus. Chaves ausentes caem
 * em defaults vazios — consumers renderizam "—" quando encontram. */
function composeSettings(rows: z.infer<typeof settingRowSchema>[]): PlatformSettings {
  const map = new Map(rows.map((r) => [r.key, r.value]));
  return {
    contratada: {
      razao_social: extractString(map, 'contratada.razao_social'),
      cnpj: extractString(map, 'contratada.cnpj'),
      nome_fantasia: extractString(map, 'contratada.nome_fantasia'),
      cidade_uf: extractString(map, 'contratada.cidade_uf'),
      email_contato: extractString(map, 'contratada.email_contato'),
      responsavel_nome: extractString(map, 'contratada.responsavel_nome'),
      responsavel_cargo: extractString(map, 'contratada.responsavel_cargo'),
    },
    proposal_footer: {
      institutional_text: extractString(map, 'proposal.footer_institutional'),
    },
    proposal_copy: {
      hero_title: extractString(map, 'proposal.hero_title'),
      hero_intro_template: extractString(map, 'proposal.hero_intro_template'),
      about_us_title: extractString(map, 'proposal.about_us_title'),
      about_us_paragraphs: extractString(map, 'proposal.about_us_paragraphs'),
      considerations_bullets: extractString(map, 'proposal.considerations_bullets'),
      post_consulting_terms_template: extractString(map, 'proposal.post_consulting_terms_template'),
    },
  };
}

export const PLATFORM_SETTINGS_KEY = ['platform-settings'] as const;

export function usePlatformSettings() {
  return useQuery<PlatformSettings, Error>({
    queryKey: PLATFORM_SETTINGS_KEY,
    // Configs mudam raramente — cache longo economiza requests.
    staleTime: 10 * 60 * 1_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('key, value')
        .in('key', [
          'contratada.razao_social',
          'contratada.cnpj',
          'contratada.nome_fantasia',
          'contratada.cidade_uf',
          'contratada.email_contato',
          'contratada.responsavel_nome',
          'contratada.responsavel_cargo',
          'proposal.footer_institutional',
          'proposal.hero_title',
          'proposal.hero_intro_template',
          'proposal.about_us_title',
          'proposal.about_us_paragraphs',
          'proposal.considerations_bullets',
          'proposal.post_consulting_terms_template',
        ]);
      if (error) throw new Error(error.message);
      return composeSettings(z.array(settingRowSchema).parse(data ?? []));
    },
  });
}
